/**
 * Attaches a tilt effect to the specified HTML elements, creating a 3D-like tilt effect on mouse movement.
 * The effect includes rotation, vertical translation (raise), and scaling. It also adds a shine effect
 * that dynamically adjusts based on the mouse position.
 *
 * @param elements - An array of HTML elements to apply the tilt effect to.
 * @param maxTiltX - The maximum tilt angle on the X-axis, in degrees. Default is 45.
 * @param maxTiltY - The maximum tilt angle on the Y-axis, in degrees. Default is 45.
 * @param raise - The vertical distance the element raises on tilt, in pixels. Default is 6.
 * @param scale - The scale factor applied to the element on tilt. Default is 1.05.
 * @returns A cleanup function that removes the event listeners and added styles from the elements.
 */
export function attachTiltEffect(elements: HTMLElement[], maxTiltX = 45, maxTiltY = 45, raise = 6, scale = 1.05) {
    function handleOnTilt(event: MouseEvent) {
        const element = event.currentTarget as HTMLElement;
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const mouseX = event.clientX - centerX;
        const mouseY = event.clientY - centerY;
        const tiltXUncapped = Math.round((maxTiltX * mouseY) / rect.height / 2);
        const tiltYUncapped = Math.round((-maxTiltY * mouseX) / rect.width / 2);
        const rotateX = Math.max(-maxTiltX, Math.min(tiltXUncapped, maxTiltX));
        const rotateY = Math.max(-maxTiltY, Math.min(tiltYUncapped, maxTiltY));

        element.style.transition = "transform 0.1s ease";
        element.style.transform = `perspective(500px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-${raise}px) scale(${scale}) translateZ(0px)`;

        const percentX = ((mouseX + rect.width / 2) / rect.width) * 100;
        const percentY = ((mouseY + rect.height / 2) / rect.height) * 100;
        element.style.setProperty("--shine-x", `${percentX}%`);
        element.style.setProperty("--shine-y", `${percentY}%`);
    }

    function handleOffTilt(event: MouseEvent) {
        const element = event.currentTarget as HTMLElement;
        element.style.transform =
            "perspective(500px) rotateX(0deg) rotateY(0deg) translateY(0px) translateZ(0px) scale(1)";
        const shineEl = element.querySelector<HTMLElement>(".tilt-shine");
        if (shineEl) {
            shineEl.style.backgroundImage = "none";
        }
    }

    const cleanup = () => {
        elements.forEach((element) => {
            element.removeEventListener("mousemove", handleOnTilt);
            element.removeEventListener("mouseout", handleOffTilt);
            element.style.transform = "";
        });
    };

    elements.forEach((element) => {
        if (element.classList.contains("games-card-game--skeleton")) {
            return;
        }
        element.addEventListener("mousemove", handleOnTilt);
        element.addEventListener("mouseout", handleOffTilt);
    });

    return cleanup;
}
